import { useEffect, useMemo, useState } from "react";
import { apiPatch } from "@/lib/apiClient";
import { CrmContactChannelsEditor } from "@/components/crm/CrmContactChannelsEditor";
import {
  assertValidCrmChannelEmails,
  channelOptionsFromLeadFields,
  channelsStateFromContact,
  contactChannelsToApiBody,
  defaultPhoneCountryIdFromLeadFields,
  type CrmContactChannelsState,
} from "@/lib/crmContactChannels";
import { crmContactDisplayName } from "@/lib/crmContactParty";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";
import { fetchCrmLeadModuleFields } from "@/lib/crmLeadModuleFieldsApi";
import {
  addressOptionsFromLeadFields,
  assertValidCrmAddresses,
  defaultAddressCountryIdFromLeadFields,
} from "@/lib/crmContactAddresses";

export type ContactEditSnapshot = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
  title?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  status?: string | null;
  emails?: import("@/lib/crmContactChannels").CrmContactEmail[];
  phones?: import("@/lib/crmContactChannels").CrmContactPhone[];
  socials?: import("@/lib/crmContactChannels").CrmContactSocial[];
  addresses?: import("@/lib/crmContactAddresses").CrmContactAddress[];
};

type CrmContactEditSectionProps = {
  accountId: string;
  contact: ContactEditSnapshot;
  onSaved: (contact: ContactEditSnapshot) => void;
};

export function CrmContactEditSection({ accountId, contact, onSaved }: CrmContactEditSectionProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState(contact.firstName || "");
  const [lastName, setLastName] = useState(contact.lastName || "");
  const [title, setTitle] = useState(contact.title || "");
  const [channels, setChannels] = useState<CrmContactChannelsState>(() =>
    channelsStateFromContact(contact),
  );
  const [leadFieldSections, setLeadFieldSections] = useState<
    Awaited<ReturnType<typeof fetchCrmLeadModuleFields>>["sections"] | null
  >(null);

  useEffect(() => {
    if (!open) return;
    setFirstName(contact.firstName || "");
    setLastName(contact.lastName || "");
    setTitle(contact.title || "");
    setChannels(channelsStateFromContact(contact));
  }, [open, contact]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const result = await fetchCrmLeadModuleFields();
      if (!cancelled) setLeadFieldSections(result.sections);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const channelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead", "contact"),
    [leadFieldSections],
  );
  const addressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const defaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const defaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      assertValidCrmChannelEmails(channels, "Contact email");
      assertValidCrmAddresses(channels.addresses, "Contact address");
      const apiChannels = contactChannelsToApiBody(channels);
      const res = await apiPatch<ContactEditSnapshot>(
        `/api/crm/contacts/${encodeURIComponent(contact.id)}?accountId=${encodeURIComponent(accountId)}`,
        {
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          title: title.trim() || null,
          email: apiChannels.email,
          phone: apiChannels.phone,
          linkedinUrl: apiChannels.linkedinUrl,
          emails: apiChannels.emails,
          phones: apiChannels.phones,
          socials: apiChannels.socials,
          addresses: apiChannels.addresses,
        },
      );
      if (!res.ok || !res.data) {
        setError(res.error || "Save failed.");
        return;
      }
      setOpen(false);
      onSaved(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 dark:border-indigo-400/50 dark:bg-indigo-900/30 dark:text-indigo-100"
      >
        {open ? "Cancel editing" : "Edit contact"}
      </button>
      {open ? (
        <>
          {error ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{error}</div>
          ) : null}
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="font-medium">First name</span>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
              <label className="text-sm">
                <span className="font-medium">Last name</span>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
              <label className="text-sm md:col-span-2">
                <span className="font-medium">Title</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
            </div>
            <CrmContactChannelsEditor
              value={channels}
              onChange={setChannels}
              emailContext="contact"
              typeOptions={channelOptions}
              defaultPhoneCountryId={defaultPhoneCountryId}
              addressTypeOptions={addressTypeOptions}
              defaultAddressCountryId={defaultAddressCountryId}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => void submit()}
                disabled={saving}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save contact"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {crmContactDisplayName(contact)} · Status {formatCrmStatusLabel(contact.status) || "—"}
        </p>
      )}
    </div>
  );
}
